import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { isEqual } from 'lodash-es'
import { ENewsEventType, ENewsStatusReverse } from '@/lib/references'
import { tabMetrics } from '@/ContextTab/services/tabMetrics'
import { apiClientV1 } from '@/ContextTab/services/apiClient'
import type { GatewaySubscriptionHubMessage } from '@/Workers/Gateway/types'
import type { NewsSubscriptionData } from '@/Workers/Gateway/gwService/types'
import type { NewsItemPublished } from '@/ContextTab/stores/types'


const GC_INTERVAL = 30_000
const EMBARGO_CHECK_INTERVAL = 2000

export const useNewsStore = defineStore('news', () => {

  const news = reactive<Record<number, NewsItemPublished>>({}) // <- published, replace when needed
  const newsUpdatedTimesMap = new WeakMap<any, number>()
  const error = ref<string | null>(null)

  async function fetchNewsLinks(id: number) {
    const response = await apiClientV1.NewsService.getNewsByNewsIdLinks({
      path: {
        news_id: id,
      },
    })

    if (response.error || !response.data.data) {
      return response.data
    }

    const dataOfNewsLinks = response.data.data

    ;['child_links', 'parent_links'].forEach((linkType) => {
      dataOfNewsLinks[linkType].forEach((newsItem) => {
        if (!news[newsItem.news.id]) {
          setNewsItem(newsItem.news.id, newsItem.news)
        }
      })
    })
    return dataOfNewsLinks
  }

  function setNewsItem(
    id: number,
    item: NewsItemPublished,
    datetime: string | number = 0, // server datetime in ISO format, if got from gateway only
  ): any { // result is required! see use of $onAction in src/ContextApp/stores/news/colors.ts

    const existingItemTime = newsUpdatedTimesMap.get(news[id]) ?? 0

    if (typeof datetime === 'string') {
      datetime = Date.parse(datetime)
      if (Number.isNaN(datetime)) {
        datetime = 0
      }
    }

    if (!datetime) {
      datetime = existingItemTime
    } else if (datetime < existingItemTime) {
      return
    }

    news[id] = item
    newsUpdatedTimesMap.set(news[id], datetime)

    if (item?.parent_links?.length) {
      const isPublished = item?.version?.status_id === ENewsStatusReverse.PUBLISHED

      // update child_links in previously fetched parent news
      item.parent_links?.forEach((link) => {
        const parentNewsItem = news[link.news_id]
        if (parentNewsItem) {
          const parentChildrenLinks = (parentNewsItem.child_links ?? [])

          const currentItemLinkIndex = parentChildrenLinks.findIndex((link) => link?.news_id === id)

          const linkToCurrentItem = {
            ...(parentChildrenLinks[currentItemLinkIndex] ?? {}),
            type_id: link.type_id,
            news_id: item.id,
            is_published: isPublished,
            is_disposed: !!item.disposer,
          }

          if (currentItemLinkIndex === -1) {
            parentChildrenLinks.push(linkToCurrentItem)
          } else {
            parentChildrenLinks.splice(currentItemLinkIndex, 1, linkToCurrentItem)
          }

          parentNewsItem.child_links = parentChildrenLinks
        }
      })
    }

    return item
  }

  async function setNewsItemAsRead(id: number) {
    const response = await apiClientV1.NewsService.postNewsByNewsIdRead({ path: { news_id: id } })

    if (!response.error && response.data?.data) {
      setNewsItem(id, response.data.data as any)
    }
  }

  // Embargo handling -------------------------------------

  const expired = reactive<number[]>([])

  const embargoNews = computed(() =>
    Object.values(news).filter((item) => {
      const version = item.version
      return (version && version.publish_at)
    }),
  )

  // can't be computed, as depend on current time
  function checkEmbargo() {
    const currentTime = new Date()
    const ids = embargoNews.value
      .filter((item) => {
        const publishTime = new Date(item.version?.publish_at ?? '3000-01-01')
        return (publishTime <= currentTime && !item.version?.is_postponed)
      })
      .map((item) => item.id)

    if (!isEqual(expired, ids)) {
      expired.length = 0
      expired.push(...ids)
    }
  }

  setInterval(checkEmbargo, EMBARGO_CHECK_INTERVAL)


  // Garbage collecting: delete news, not registered in sets -----------

  const gcSets: WeakRef<number[]>[] = []

  function registerNewsSetForGc(set: number[]) {
    gcSets.push(new WeakRef(set))
  }

  function executeGc() {
    let allIds: number[] = []

    gcSets.forEach((setRef) => {
      const set = setRef.deref()
      if (set) {
        allIds.push(...set)
      }
    })

    // leave unique
    allIds = Array.from(new Set(allIds))

    const countNewsObjects = Object.keys(news).length

    if (countNewsObjects > 2000) {
      tabMetrics.sendMessage(`GC: too much news objects: ${countNewsObjects}`)
    }

    console.log(`GC: news: ${countNewsObjects}, refs: ${allIds.length}`)

    // theoretically, we may stuck here,
    //  but no way to split it into chunks or execute in parallel: data would change
    console.time('GC processing')
    Object.keys(news).forEach((newsId) => {
      if (!allIds.includes(Number(newsId))) {
        delete news[newsId]
      }
    })
    console.timeEnd('GC processing')
  }

  setInterval(executeGc, GC_INTERVAL)

  function clearStore() {
    Object.keys(news).forEach((newsId) => {
      delete news[newsId]
    })
    // we may also clear sets here,
    // but 1) it will be implicit and 2) we should reset sets' store state
  }

  // Subscription handling -------------------

  function onGwNewsMessage(message: GatewaySubscriptionHubMessage) {
    if (!message.payload.data) {
      return
    }

    const data = message.payload.data as NewsSubscriptionData

    // TODO: Ошибка в типах, проверить почему
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const newsEventTypeName = ENewsEventType[message.payload.data.type_id]

    if (newsEventTypeName === 'DELETED') {
      delete news[data.news.id]
    } else {
      setNewsItem(
        data.news.id,
        data.news,
        message.payload.datetime,
      )
    }
  }

  return {
    news,
    error,
    setNewsItem,
    setNewsItemAsRead,
    fetchNewsLinks,
    expired,

    registerNewsSetForGc,
    clearStore,

    onGwNewsMessage,
  }
})
